import { createContext, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useAppContext } from '../redux/slices/appContext'
import { replaceValues, replaceValuesEx } from '../utils/replacers'
import { useNormalizeDates } from '../api/coreData'
import { DATE_FORMAT, DATE_FORMAT_DASH } from '../constants'

dayjs.extend(utc)

export const getRelativeDateRanges = (relativeToDate) => {
  const currentDate = relativeToDate || dayjs().utc().format(DATE_FORMAT_DASH)
  const date = dayjs().utc(currentDate)

  // This previously was in ISO format with time - but the places we need these dates do not need times
  return {
    yearStart: date.startOf('year').format(DATE_FORMAT_DASH),
    lastYearStart: date.add(-1, 'year').startOf('year').format(DATE_FORMAT_DASH),
    yearEnd: date.endOf('year').format(DATE_FORMAT_DASH),
    prevYearStart: date.add(-1, 'year').startOf('year').format(DATE_FORMAT_DASH),
    prevYearEnd: date.add(-1, 'year').endOf('year').format(DATE_FORMAT_DASH),
    quarterStart: date.startOf('quarter').format(DATE_FORMAT_DASH),
    quarterEnd: date.endOf('quarter').format(DATE_FORMAT_DASH),
    prevQuarterStart: date.add(-1, 'quarter').startOf('quarter').format(DATE_FORMAT_DASH),
    prevQuarterEnd: date.add(-1, 'quarter').endOf('quarter').format(DATE_FORMAT_DASH),
    monthStart: date.startOf('month').format(DATE_FORMAT_DASH),
    monthEnd: date.endOf('month').format(DATE_FORMAT_DASH),
    prevMonthStart: date.add(-1, 'month').startOf('month').format(DATE_FORMAT_DASH),
    prevMonthEnd: date.add(-1, 'month').endOf('month').format(DATE_FORMAT_DASH),
    weekStart: date.startOf('week').format(DATE_FORMAT_DASH),
    weekEnd: date.endOf('week').format(DATE_FORMAT_DASH),
    prevWeekStart: date.add(-1, 'week').startOf('week').format(DATE_FORMAT_DASH),
    prevWeekEnd: date.add(-1, 'week').endOf('week').format(DATE_FORMAT_DASH),
    nextWeekStart: date.add(1, 'week').startOf('week').format(DATE_FORMAT_DASH),
    nextWeekEnd: date.add(1, 'week').endOf('week').format(DATE_FORMAT_DASH),
    today: date.startOf('day').format(DATE_FORMAT_DASH),
    yesterday: date.add(-1, 'day').format(DATE_FORMAT_DASH),
    in1day: date.add(1, 'day').startOf('day').format(DATE_FORMAT_DASH),
    in7days: date.add(7, 'day').startOf('day').format(DATE_FORMAT_DASH),
    in30days: date.add(30, 'day').startOf('day').format(DATE_FORMAT_DASH),
    in1week: date.add(1, 'week').startOf('day').format(DATE_FORMAT_DASH),
    in1month: date.add(1, 'month').startOf('day').format(DATE_FORMAT_DASH),
    in1year: date.add(1, 'year').startOf('day').format(DATE_FORMAT_DASH)
  }
}

export const relativeDateRanges = getRelativeDateRanges()

export const AbundanceEngineParameterContext = createContext({})

export const useAbundanceEngineParameterContextManager = (additionalParams = {}) => {
  const appContext = useAppContext()
  const history = useHistory()

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(history.location.search)
    return Object.fromEntries(searchParams.entries())
  }, [history])

  const lastBusinessDayQuery = useMemo(
    () => {
      const rawDate = dayjs.utc().subtract(1, 'day').format(DATE_FORMAT)
      return {
        query: {
          dateRanges: {
            lastBusinessDate: { startDate: rawDate, endDate: rawDate }
          }
        },
        queryOptions: {
          mapper: (data) => {
            return data?.lastBusinessDate?.endDate
          }
        }
      }
    },
    []
  )
  const { data: lastBusinessDate } = useNormalizeDates(
    lastBusinessDayQuery.query,
    lastBusinessDayQuery.queryOptions
  )

  const result = {
    ...queryParams,
    ...additionalParams,
    externalUserId: appContext.userId,
    userId: appContext.userProfile?.internalId,
    clientId: appContext.clientId,
    advisorId: additionalParams?.advisorId !== undefined
      ? additionalParams?.advisorId
      : appContext.advisorId,
    minDate: appContext.availableDates?.min,
    maxDate: appContext.availableDates?.mainDate ?? appContext.availableDates?.max,
    mainDate: appContext.availableDates?.mainDate ?? null,
    lastBusinessDay: lastBusinessDate,
    ...relativeDateRanges
  }

  return result
}

export const useAbundanceEngineParameters = () => {
  const parameterContext = useContext(AbundanceEngineParameterContext)

  return parameterContext
}

export const useParameterReplacement = (toReplace, definitions = {}) => {
  const abundanceParams = useAbundanceEngineParameters()
  return useMemo(() => {
    const mergedDefinitions = {
      ...(abundanceParams || {}),
      ...(definitions || {})
    }
    return replaceValues(toReplace || {}, mergedDefinitions)
  }, [toReplace, abundanceParams, definitions])
}

export const useParameterReplacementEx = (toReplace, definitions = {}) => {
  const abundanceParams = useAbundanceEngineParameters()
  return useMemo(() => {
    const mergedDefinitions = {
      ...(abundanceParams || {}),
      ...(definitions || {})
    }
    return replaceValuesEx(toReplace || {}, mergedDefinitions)
  }, [toReplace, abundanceParams, definitions])
}
